import { createContext, useContext, useReducer } from "react";
import type { TutorialLevel, TutorialPhase } from "./tutorial-data";

export interface TutorialState {
  levelData?: TutorialLevel;
  phaseData?: TutorialPhase; // This is levelData.phases[phaseIndex] repackaged for convenience
  phaseIndex: number;
  isInTutorial: boolean;
}

const INITIAL_TUTORIAL_STATE: TutorialState = {
  phaseIndex: 0,
  isInTutorial: false,
};

export const TutorialAction = {
  START_TUTORIAL: "START_TUTORIAL",
  ADVANCE_TUTORIAL: "ADVANCE_TUTORIAL",
  FINISH_TUTORIAL: "FINISH_TUTORIAL",
} as const;

export type TutorialAction =
  | {
      type: typeof TutorialAction.START_TUTORIAL;
      payload: { levelData: TutorialLevel };
    }
  | { type: typeof TutorialAction.ADVANCE_TUTORIAL }
  | { type: typeof TutorialAction.FINISH_TUTORIAL };

function tutorialReducer(state: TutorialState, action: TutorialAction) {
  switch (action.type) {
    case TutorialAction.START_TUTORIAL: {
      return {
        ...INITIAL_TUTORIAL_STATE,
        levelData: action.payload.levelData,
        phaseData: action.payload.levelData.phases[0],
        isInTutorial: true,
      };
    }
    case TutorialAction.ADVANCE_TUTORIAL: {
      const newPhaseIndex = Math.min(
        (state.levelData?.phases.length ?? 1) - 1,
        state.phaseIndex + 1,
      );
      return {
        ...state,
        phaseIndex: newPhaseIndex,
        phaseData: state.levelData?.phases[newPhaseIndex],
      };
    }
    case TutorialAction.FINISH_TUTORIAL: {
      return {
        ...state,
        phaseIndex: 0,
        phaseData: undefined,
        isInTutorial: false,
      }
    }
    default:
      return state;
  }
}

export function useTutorialReducer() {
  return useReducer(tutorialReducer, INITIAL_TUTORIAL_STATE);
}

export const TutorialActionDispatchContext = createContext<
  React.Dispatch<TutorialAction>
>(() => {});

export function useTutorialActionDispatch() {
  return useContext(TutorialActionDispatchContext);
}

export const TutorialContext = createContext<TutorialState>(
  INITIAL_TUTORIAL_STATE,
);

export default function useTutorialState() {
  return useContext(TutorialContext);
}
